import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  MapPin,
  Mail,
  Calendar,
  Search,
  Download,
} from "lucide-react";
import { useAuth } from "../App";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

/**
 * /admin/waitlist — Expansion Waitlist admin page.
 * Shows individual sign-ups (email, ZIP, joined date, status) plus per-ZIP
 * aggregate counts to help decide which markets to expand into next.
 */
export default function AdminWaitlist() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [entries, setEntries] = useState([]);
  const [zipCounts, setZipCounts] = useState([]);
  const [search, setSearch] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const [sortByCount, setSortByCount] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotal(res.data.total || 0);
      setEntries(res.data.entries || []);
      setZipCounts(res.data.zip_counts || []);
    } catch (err) {
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredEntries = entries.filter((e) => {
    if (zipFilter && !e.zip_code.includes(zipFilter)) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!e.email.toLowerCase().includes(s) && !e.zip_code.includes(s)) {
        return false;
      }
    }
    return true;
  });

  const sortedZipCounts = [...zipCounts].sort((a, b) =>
    sortByCount ? b.count - a.count : a.zip_code.localeCompare(b.zip_code)
  );

  const exportCsv = () => {
    const header = "email,zip_code,source,status,created_at\n";
    const rows = filteredEntries
      .map(
        (e) =>
          `${e.email},${e.zip_code},"${e.source || ""}",${e.status || ""},${e.created_at || ""}`
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FA]" data-testid="admin-waitlist-page">
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Link to="/admin" data-testid="admin-waitlist-back">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </Link>
          <div>
            <h1 className="font-manrope font-bold text-2xl text-[#1F2933]">
              Expansion Waitlist
            </h1>
            <p className="text-sm text-[#1F2933]/60">
              Homeowners waiting for ContractorHub in their area.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="waitlist-total-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#1F2933]/60">
                Total sign-ups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Users className="w-7 h-7 text-[#0FA3B1]" />
                <span className="text-3xl font-bold text-[#1F2933]">{total}</span>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="waitlist-zips-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#1F2933]/60">
                Unique ZIP codes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <MapPin className="w-7 h-7 text-[#0FA3B1]" />
                <span className="text-3xl font-bold text-[#1F2933]">
                  {zipCounts.length}
                </span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#1F2933]/60">
                Export
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={filteredEntries.length === 0}
                className="w-full"
                data-testid="waitlist-export-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ZIP counts widget */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Interest by ZIP</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSortByCount((prev) => !prev)}
              data-testid="waitlist-toggle-sort"
            >
              Sort: {sortByCount ? "Most Interested" : "ZIP (asc)"}
            </Button>
          </CardHeader>
          <CardContent>
            {sortedZipCounts.length === 0 ? (
              <p className="text-sm text-[#1F2933]/60">No sign-ups yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sortedZipCounts.map((row) => (
                  <button
                    key={row.zip_code}
                    onClick={() =>
                      setZipFilter((prev) => (prev === row.zip_code ? "" : row.zip_code))
                    }
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                      zipFilter === row.zip_code
                        ? "border-[#0FA3B1] bg-[#0FA3B1] text-white"
                        : "border-gray-200 bg-white text-[#1F2933] hover:border-[#0FA3B1]"
                    }`}
                    data-testid={`zip-count-${row.zip_code}`}
                  >
                    {row.zip_code} <span className="opacity-70 ml-1">×{row.count}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filter/search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or ZIP…"
              className="pl-9"
              data-testid="waitlist-search"
            />
          </div>
          {zipFilter && (
            <Button
              variant="outline"
              onClick={() => setZipFilter("")}
              data-testid="waitlist-clear-zip-filter"
            >
              Clear ZIP filter: {zipFilter}
            </Button>
          )}
        </div>

        {/* Entries table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-[#1F2933]/60">Loading…</p>
            ) : filteredEntries.length === 0 ? (
              <p className="p-6 text-sm text-[#1F2933]/60">No entries match your filter.</p>
            ) : (
              <Table data-testid="waitlist-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>ZIP</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.waitlist_id} data-testid={`waitlist-row-${entry.waitlist_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-[#1F2933]/40" />
                          <span className="text-sm">{entry.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{entry.zip_code}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-[#1F2933]/70">
                          <Calendar className="w-3.5 h-3.5 text-[#1F2933]/40" />
                          {new Date(entry.created_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-100 text-amber-800">
                          {entry.status || "Waiting"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
